import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Badge } from 'react-bootstrap';
import { IconPlus, IconTicket, IconRefresh } from '@tabler/icons-react';
import { api } from '../../shared/api';
import { useRealtimeEvents } from '@shared/useRealtimeEvents';
import { useQueryClient } from '@tanstack/react-query';
import PageShell from '../../components/layout/PageShell';
import PageSection from '../../components/layout/PageSection';

const STATUS_VARIANT = {
  open:            'danger',
  pending:         'warning',
  awaiting_reply:  'info',
  resolved:        'success',
  closed:          'secondary',
};

const PRIORITY_VARIANT = {
  low:    'secondary',
  medium: 'primary',
  high:   'warning',
  urgent: 'danger',
};

export default function SupportPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('');

  const { data: tickets = [], isLoading, isError } = useQuery({
    queryKey: ['support', 'tickets', statusFilter],
    queryFn: () => api.get(`support/api/tickets/${statusFilter ? `?status=${statusFilter}` : ''}`),
  });

  // Invalidate ticket list on real-time model changes
  useRealtimeEvents((event) => {
    if (event.model === 'support.ticket') {
      queryClient.invalidateQueries({ queryKey: ['support', 'tickets'] });
    }
  });

  const statuses = ['', 'open', 'pending', 'awaiting_reply', 'resolved', 'closed'];

  return (
    <PageShell
      pretitle="Support"
      title={<><IconTicket className="me-2" size={22} />{t('support.title', 'Support')}</>}
      actions={
        <Link to="/support/new" className="btn btn-primary">
          <IconPlus size={16} className="me-1" />
          {t('support.newTicket', 'New ticket')}
        </Link>
      }
    >
      {/* Status filter */}
      <div className="d-flex gap-2 flex-wrap">
        {statuses.map((s) => (
          <button
            key={s || 'all'}
            className={`btn btn-sm ${statusFilter === s ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => setStatusFilter(s)}
          >
            {s ? t(`support.status.${s}`, s) : t('support.allStatuses', 'All')}
          </button>
        ))}
      </div>

      {/* Ticket list */}
      <PageSection bodyClassName="p-0">
        {isLoading && (
          <div className="p-4 text-center text-muted">
            <IconRefresh className="me-2 spin" size={18} />
            {t('common.loading', 'Loading…')}
          </div>
        )}
        {isError && (
          <div className="p-4 text-center text-danger">
            {t('common.loadError', 'Failed to load tickets.')}
          </div>
        )}
        {!isLoading && !isError && tickets.length === 0 && (
          <div className="p-4 text-center text-muted">
            {t('support.noTickets', 'No tickets yet.')}
          </div>
        )}
        {!isLoading && tickets.map((ticket) => (
          <Link
            key={ticket.reference}
            to={`/support/${ticket.reference}`}
            className="d-flex align-items-start gap-3 p-3 border-bottom text-decoration-none text-reset ticket-row"
            style={{ transition: 'background .1s' }}
          >
            <div className="flex-fill min-width-0">
              <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                <span className="fw-semibold text-truncate">{ticket.title}</span>
                <Badge bg={STATUS_VARIANT[ticket.status] || 'secondary'} className="text-capitalize">
                  {t(`support.status.${ticket.status}`, ticket.status)}
                </Badge>
                <Badge bg={PRIORITY_VARIANT[ticket.priority] || 'secondary'} className="text-capitalize">
                  {t(`support.priority.${ticket.priority}`, ticket.priority)}
                </Badge>
              </div>
              <div className="text-muted small">
                <span className="me-3">#{ticket.reference}</span>
                {ticket.category && <span className="me-3">{ticket.category.name}</span>}
                {ticket.assigned_to_name && (
                  <span className="me-3">{t('support.assignedTo', 'Agent')}: {ticket.assigned_to_name}</span>
                )}
                <span>{new Date(ticket.created_at).toLocaleDateString()}</span>
                {ticket.message_count > 0 && (
                  <span className="ms-3">{ticket.message_count} {t('support.replies', 'replies')}</span>
                )}
              </div>
            </div>
          </Link>
        ))}
      </PageSection>
    </PageShell>
  );
}
